import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { UniverseNode } from '../../../../types/targetUniverse';

// ─── Colours ───
const CLR = {
  projectTarget: 0x22d3ee,
  safe: 0x10b981,
  warning: 0xf59e0b,
  danger: 0xef4444,
  pathway: 0x8b5cf6,
  safety: 0xf472b6,
};

function riskColorHex(selLog: number): number {
  if (selLog >= 2) return CLR.safe;
  if (selLog >= 1) return CLR.warning;
  return CLR.danger;
}

function riskColorStr(selLog: number): string {
  if (selLog >= 2) return '#10b981';
  if (selLog >= 1) return '#f59e0b';
  return '#ef4444';
}

function riskLabel(selLog: number): string {
  if (selLog >= 2) return 'SAFE';
  if (selLog >= 1) return 'WATCH';
  return 'RISK';
}

// ─── Props ───
interface Props {
  nodes: UniverseNode[];
  selectedTarget: string | null;
  onSelectTarget: (id: string | null) => void;
  projectPIC50: number;
}

const TargetUniverse3D: React.FC<Props> = ({ nodes, selectedTarget, onSelectTarget, projectPIC50 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const targetMeshesRef = useRef<Map<string, THREE.Mesh>>(new Map());
  const connectionLinesRef = useRef<THREE.Line[]>([]);
  const glowMeshRef = useRef<THREE.Mesh | null>(null);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
  const animationFrameRef = useRef<number | null>(null);
  const hasUserInteractedRef = useRef<boolean>(false);
  const starsRef = useRef<THREE.Points | null>(null);

  const [hoveredTarget, setHoveredTarget] = useState<UniverseNode | null>(null);
  const [manualRotate, setManualRotate] = useState(false);
  const [webGLError, setWebGLError] = useState<string | null>(null);

  // Follow system theme toggle
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains('theme-dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains('theme-dark'));
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const selectedNode = useMemo(
    () => nodes.find(n => n.id === selectedTarget) || null,
    [nodes, selectedTarget],
  );

  // ═══════════════════════════════════════════════════════════
  // Scene setup — runs once
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0f172a);

    // Starfield
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 500;
    const starPositions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount * 3; i += 3) {
      starPositions[i] = (Math.random() - 0.5) * 300;
      starPositions[i + 1] = (Math.random() - 0.5) * 300;
      starPositions[i + 2] = (Math.random() - 0.5) * 300;
    }
    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const starMaterial = new THREE.PointsMaterial({ color: 0x444466, size: 0.5 });
    const stars = new THREE.Points(starGeometry, starMaterial);
    scene.add(stars);
    starsRef.current = stars;
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.set(0, 15, 60);
    cameraRef.current = camera;

    // Renderer — wrapped in try/catch for WebGL unavailability
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch (e) {
      setWebGLError("3D view unavailable — your browser doesn't support WebGL");
      return;
    }
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Listen for WebGL context loss
    const handleContextLost = (e: Event) => {
      e.preventDefault();
      setWebGLError("3D view lost — switch to 2D Scatter for a stable view");
    };
    renderer.domElement.addEventListener('webglcontextlost', handleContextLost);

    // Controls — same as PatentCollisionMap3D
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.4;
    controls.zoomSpeed = 0.6;
    controls.panSpeed = 0.5;
    controls.minDistance = 15;
    controls.maxDistance = 200;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.3;
    controlsRef.current = controls;

    // Stop auto-rotate permanently on first user interaction
    const stopAutoRotate = () => {
      hasUserInteractedRef.current = true;
      controls.autoRotate = false;
    };
    renderer.domElement.addEventListener('pointerdown', stopAutoRotate);
    renderer.domElement.addEventListener('wheel', stopAutoRotate);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const dl1 = new THREE.DirectionalLight(0xffffff, 0.6);
    dl1.position.set(50, 50, 50);
    scene.add(dl1);
    const dl2 = new THREE.DirectionalLight(0x6366f1, 0.3);
    dl2.position.set(-50, -20, -30);
    scene.add(dl2);

    // Animation loop
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      // Pulse project target glow
      if (glowMeshRef.current) {
        const scale = 1 + Math.sin(performance.now() * 0.002) * 0.3;
        glowMeshRef.current.scale.setScalar(scale);
      }

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('pointerdown', stopAutoRotate);
      renderer.domElement.removeEventListener('wheel', stopAutoRotate);
      controls.dispose();
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh || obj instanceof THREE.Line || obj instanceof THREE.Points) {
          obj.geometry?.dispose();
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else if (obj.material) obj.material.dispose();
        }
        if (obj instanceof THREE.Sprite) {
          obj.material?.dispose();
          (obj.material as THREE.SpriteMaterial)?.map?.dispose();
        }
      });
      targetMeshesRef.current.clear();
      connectionLinesRef.current = [];
      renderer.domElement.removeEventListener('webglcontextlost', handleContextLost);
      renderer.dispose();
      renderer.forceContextLoss();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
    };
  }, []);

  // Sync manual rotate toggle
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = manualRotate;
      controlsRef.current.autoRotateSpeed = 0.3;
    }
  }, [manualRotate]);

  // Theme switching — update scene background and star color
  useEffect(() => {
    if (!sceneRef.current) return;
    sceneRef.current.background = new THREE.Color(isDarkMode ? 0x0f172a : 0xf8fafc);
    if (starsRef.current) {
      (starsRef.current.material as THREE.PointsMaterial).color.set(isDarkMode ? 0x444466 : 0xd0d4dc);
    }
  }, [isDarkMode]);

  // ═══════════════════════════════════════════════════════════
  // Create target spheres, pathway lines
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (!sceneRef.current) return;
    const scene = sceneRef.current;

    // Clear existing meshes
    targetMeshesRef.current.forEach((mesh) => {
      mesh.geometry?.dispose();
      (mesh.material as THREE.Material)?.dispose();
      scene.remove(mesh);
    });
    targetMeshesRef.current.clear();

    connectionLinesRef.current.forEach((line) => {
      line.geometry?.dispose();
      (line.material as THREE.Material)?.dispose();
      scene.remove(line);
    });
    connectionLinesRef.current = [];

    if (glowMeshRef.current) {
      glowMeshRef.current.geometry?.dispose();
      (glowMeshRef.current.material as THREE.Material)?.dispose();
      scene.remove(glowMeshRef.current);
      glowMeshRef.current = null;
    }

    // Remove old sprites & glow objects
    const toRemove = scene.children.filter(c => c.userData.isTargetLabel || c.userData.isProjectGlow);
    toRemove.forEach(c => {
      if (c instanceof THREE.Sprite) {
        c.material?.dispose();
        (c.material as THREE.SpriteMaterial)?.map?.dispose();
      }
      if (c instanceof THREE.Mesh || c instanceof THREE.LineSegments) {
        c.geometry?.dispose();
        (c.material as THREE.Material)?.dispose();
      }
      scene.remove(c);
    });

    // ─── Compute 3D positions using golden-angle sphere distribution ───
    const nonProject = nodes.filter(n => !n.isProjectTarget);
    const total = nonProject.length;
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    interface Positioned { node: UniverseNode; px: number; py: number; pz: number; }
    const positioned: Positioned[] = nodes.map(node => {
      if (node.isProjectTarget) {
        return { node, px: 0, py: 0, pz: 0 };
      }
      const selLog = projectPIC50 - node.pIC50;
      const dist = 15 + selLog * 15;
      const idx = nonProject.indexOf(node);
      const theta = goldenAngle * idx;
      const phi = Math.acos(1 - 2 * (idx + 0.5) / total);
      return {
        node,
        px: dist * Math.sin(phi) * Math.cos(theta),
        py: dist * Math.sin(phi) * Math.sin(theta),
        pz: dist * Math.cos(phi),
      };
    });

    // ─── Create spheres ───
    const maxCompoundCount = Math.max(1, ...nodes.map(n => n.compoundCount || 1));
    positioned.forEach(({ node: target, px, py, pz }) => {
      if (target.isProjectTarget) {
        // Project target — semi-transparent solid with wireframe overlay
        const geom = new THREE.SphereGeometry(5, 32, 32);
        const mat = new THREE.MeshPhongMaterial({
          color: CLR.projectTarget,
          emissive: CLR.projectTarget,
          emissiveIntensity: 0.4,
          transparent: true,
          opacity: 0.35,
        });
        const mesh = new THREE.Mesh(geom, mat);
        mesh.position.set(0, 0, 0);
        mesh.userData = { target, type: 'target' };
        scene.add(mesh);
        targetMeshesRef.current.set(target.id, mesh);

        // Wireframe overlay (same pattern as patent territories)
        const edges = new THREE.EdgesGeometry(geom);
        const wireframeMat = new THREE.LineBasicMaterial({
          color: CLR.projectTarget,
          transparent: true,
          opacity: 0.6,
        });
        const wireframeOverlay = new THREE.LineSegments(edges, wireframeMat);
        wireframeOverlay.userData = { isProjectGlow: true };
        scene.add(wireframeOverlay);

        // Pulsing glow sphere
        const glowGeom = new THREE.SphereGeometry(7, 32, 32);
        const glowMat = new THREE.MeshBasicMaterial({
          color: CLR.projectTarget,
          transparent: true,
          opacity: 0.15,
        });
        const glow = new THREE.Mesh(glowGeom, glowMat);
        glow.userData = { isProjectGlow: true };
        scene.add(glow);
        glowMeshRef.current = glow;

        // Permanent label sprite
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = 256;
        canvas.height = 64;
        ctx.font = 'bold 28px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#22d3ee';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(target.name, 128, 32);
        const texture = new THREE.CanvasTexture(canvas);
        const spriteMat = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0.9 });
        const sprite = new THREE.Sprite(spriteMat);
        sprite.position.set(0, 8, 0);
        sprite.scale.set(8, 2, 1);
        sprite.userData = { isTargetLabel: true };
        scene.add(sprite);
      } else {
        // Off-target sphere
        const selLog = projectPIC50 - target.pIC50;
        const isSafety = target.safetyFlag &&
          (target.safetyFlag.toLowerCase().includes('herg') || target.safetyFlag.toLowerCase().includes('cyp'));
        const color = isSafety ? CLR.safety : riskColorHex(selLog);
        const countNorm = (target.compoundCount || 1) / maxCompoundCount;
        const radius = (0.3 + countNorm * 0.7) * 4.5;

        const geom = new THREE.SphereGeometry(radius, 16, 16);
        const mat = new THREE.MeshPhongMaterial({
          color,
          emissive: color,
          emissiveIntensity: 0.3,
          transparent: true,
          opacity: 0.7,
          wireframe: true,
        });
        const mesh = new THREE.Mesh(geom, mat);
        mesh.position.set(px, py, pz);
        mesh.userData = { target, type: 'target' };
        scene.add(mesh);
        targetMeshesRef.current.set(target.id, mesh);
      }
    });

    // ─── Auto-center camera to fit all data points ───
    if (cameraRef.current && controlsRef.current && positioned.length > 0) {
      const bbox = new THREE.Box3();
      positioned.forEach(({ px, py, pz }) => {
        bbox.expandByPoint(new THREE.Vector3(px, py, pz));
      });
      const center = new THREE.Vector3();
      bbox.getCenter(center);
      const size = new THREE.Vector3();
      bbox.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = cameraRef.current.fov * (Math.PI / 180);
      const cameraDistance = (maxDim / 2) / Math.tan(fov / 2) * 1.4;

      controlsRef.current.target.copy(center);
      cameraRef.current.position.set(
        center.x,
        center.y + cameraDistance * 0.3,
        center.z + cameraDistance
      );
      cameraRef.current.lookAt(center);
      controlsRef.current.update();

      // Update bounds to prevent losing the data
      controlsRef.current.maxDistance = cameraDistance * 3;
      controlsRef.current.minDistance = cameraDistance * 0.3;
    }

    // ─── Pathway connection lines (purple) ───
    for (let i = 0; i < positioned.length; i++) {
      for (let j = i + 1; j < positioned.length; j++) {
        const shared = (positioned[i].node.pathways || [])
          .filter(p => (positioned[j].node.pathways || []).includes(p));
        if (shared.length > 0) {
          const points = [
            new THREE.Vector3(positioned[i].px, positioned[i].py, positioned[i].pz),
            new THREE.Vector3(positioned[j].px, positioned[j].py, positioned[j].pz),
          ];
          const lineGeom = new THREE.BufferGeometry().setFromPoints(points);
          const lineMat = new THREE.LineBasicMaterial({
            color: CLR.pathway,
            transparent: true,
            opacity: Math.min(0.5, 0.15 + shared.length * 0.1),
          });
          const line = new THREE.Line(lineGeom, lineMat);
          scene.add(line);
          connectionLinesRef.current.push(line);
        }
      }
    }
  }, [nodes, projectPIC50]);

  // ─── Highlight selected target ───
  useEffect(() => {
    targetMeshesRef.current.forEach((mesh, id) => {
      const isSelected = selectedTarget === id;
      const mat = mesh.material as THREE.MeshPhongMaterial;
      const isProject = mesh.userData.target?.isProjectTarget;
      mat.emissiveIntensity = isSelected ? 0.6 : (isProject ? 0.4 : 0.2);
      if (isSelected && !isProject) {
        mesh.scale.setScalar(1.3);
      } else {
        mesh.scale.setScalar(1);
      }
    });
  }, [selectedTarget]);

  // ─── Raycasting: hover ───
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!containerRef.current || !cameraRef.current || !sceneRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const meshes = Array.from(targetMeshesRef.current.values());
    const intersects = raycasterRef.current.intersectObjects(meshes);

    if (intersects.length > 0) {
      const t = intersects[0].object.userData.target as UniverseNode;
      setHoveredTarget(t);
      containerRef.current.style.cursor = 'pointer';
    } else {
      setHoveredTarget(null);
      containerRef.current.style.cursor = 'grab';
    }
  }, []);

  // ─── Raycasting: click ───
  const handleClick = useCallback((event: React.MouseEvent) => {
    if (!containerRef.current || !cameraRef.current || !sceneRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const meshes = Array.from(targetMeshesRef.current.values());
    const intersects = raycasterRef.current.intersectObjects(meshes);

    if (intersects.length > 0) {
      const t = intersects[0].object.userData.target as UniverseNode;
      onSelectTarget(t.id);
    } else {
      onSelectTarget(null);
    }
  }, [onSelectTarget]);

  // Sidebar theme colors
  const tp = isDarkMode ? '#e2e8f0' : '#1e293b';
  const td = isDarkMode ? '#475569' : '#94a3b8';
  const sb = isDarkMode ? '#1e293b' : '#e2e8f0';

  // ═══════════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════════
  if (webGLError) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ height: 500, background: 'var(--card-bg, #161b22)', borderRadius: 12 }}>
        <div className="text-center px-8 py-12">
          <svg className="w-12 h-12 mx-auto mb-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary, #e2e8f0)' }}>WebGL Unavailable</h3>
          <p className="text-sm" style={{ color: 'var(--text-muted, #475569)' }}>{webGLError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0" style={{ height: 500 }}>
      {/* 3D Canvas */}
      <div
        ref={containerRef}
        className="flex-1 relative"
        onMouseMove={handleMouseMove}
        onClick={handleClick}
        style={{ cursor: 'grab' }}
      >
        {/* Hover tooltip */}
        {hoveredTarget && (
          <div
            className="absolute top-4 left-4 backdrop-blur rounded-lg px-4 py-3 shadow-2xl z-10 pointer-events-none"
            style={{ backgroundColor: isDarkMode ? 'rgba(15,23,42,0.9)' : 'rgba(255,255,255,0.95)', border: `1px solid ${isDarkMode ? '#1e293b' : '#e2e8f0'}` }}
          >
            <div className="text-sm font-semibold" style={{ color: hoveredTarget.isProjectTarget ? '#22d3ee' : (isDarkMode ? '#e2e8f0' : '#1e293b') }}>
              {hoveredTarget.name}
            </div>
            <div className="text-xs mt-1" style={{ color: td }}>
              {hoveredTarget.activityNM < 1000
                ? `${Math.round(hoveredTarget.activityNM)} nM`
                : `${(hoveredTarget.activityNM / 1000).toFixed(1)} \u00B5M`}
              {' · '}pIC50 {hoveredTarget.pIC50.toFixed(1)}
            </div>
            {!hoveredTarget.isProjectTarget && (() => {
              const sl = projectPIC50 - hoveredTarget.pIC50;
              const ratio = Math.pow(10, sl);
              return (
                <div className="text-xs mt-1" style={{ color: riskColorStr(sl) }}>
                  {ratio > 1000 ? `${(ratio / 1000).toFixed(0)}K\u00D7` : `${ratio.toFixed(0)}\u00D7`} selectivity · {riskLabel(sl)}
                </div>
              );
            })()}
          </div>
        )}

        {/* Rotate toggle */}
        <button
          onClick={() => setManualRotate(!manualRotate)}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all z-20"
          style={{
            background: manualRotate ? 'rgba(20,184,166,0.3)' : 'rgba(51,65,85,0.8)',
            color: manualRotate ? '#14b8a6' : '#94a3b8',
            border: 'none', cursor: 'pointer',
          }}
          title={manualRotate ? 'Stop rotation' : 'Auto-rotate'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
          </svg>
        </button>

        {/* Controls hint */}
        <div
          className="absolute bottom-4 left-4 text-xs pointer-events-none backdrop-blur rounded px-2 py-1"
          style={{ color: isDarkMode ? '#64748b' : '#94a3b8', backgroundColor: isDarkMode ? 'rgba(15,23,42,0.7)' : 'rgba(255,255,255,0.8)' }}
        >
          Drag to rotate &middot; Scroll to zoom &middot; Click target for details
        </div>
      </div>

      {/* ─── Right sidebar — Target Inspector ─── */}
      <div className="w-72 overflow-y-auto" style={{ backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', borderLeft: `1px solid ${sb}` }}>
        {selectedNode ? (
          <div style={{ padding: 16, fontFamily: "'JetBrains Mono', monospace" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: selectedNode.isProjectTarget ? '#22d3ee' : tp, marginBottom: 4 }}>
              {selectedNode.name}
            </div>
            {selectedNode.fullName && selectedNode.fullName !== selectedNode.name && (
              <div style={{ color: td, fontSize: 10, marginBottom: 12 }}>{selectedNode.fullName}</div>
            )}

            {selectedNode.uniprot && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: td, fontSize: 9, marginBottom: 2 }}>UNIPROT</div>
                <a href={`https://www.uniprot.org/uniprot/${selectedNode.uniprot}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', fontSize: 12 }}>
                  {selectedNode.uniprot}
                </a>
              </div>
            )}

            {/* Activity + Selectivity cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 12 }}>
              <div style={{ padding: 8, background: 'rgba(59,130,246,0.08)', borderRadius: 6, border: '1px solid rgba(59,130,246,0.15)' }}>
                <div style={{ color: td, fontSize: 9, marginBottom: 2 }}>ACTIVITY</div>
                <div style={{ color: '#3b82f6', fontSize: 15, fontWeight: 700 }}>
                  {selectedNode.activityNM < 1000 ? `${Math.round(selectedNode.activityNM)} nM` : `${(selectedNode.activityNM / 1000).toFixed(1)} \u00B5M`}
                </div>
                <div style={{ color: td, fontSize: 9 }}>pIC50 = {selectedNode.pIC50.toFixed(1)}</div>
              </div>
              {!selectedNode.isProjectTarget && (() => {
                const sl = projectPIC50 - selectedNode.pIC50;
                const ratio = Math.pow(10, sl);
                const c = riskColorStr(sl);
                return (
                  <div style={{ padding: 8, background: `${c}15`, borderRadius: 6, border: `1px solid ${c}30` }}>
                    <div style={{ color: td, fontSize: 9, marginBottom: 2 }}>SELECTIVITY</div>
                    <div style={{ color: c, fontSize: 15, fontWeight: 700 }}>
                      {ratio > 1000 ? `${(ratio / 1000).toFixed(0)}K\u00D7` : `${ratio.toFixed(0)}\u00D7`}
                    </div>
                    <div style={{ color: c, fontSize: 9, fontWeight: 600 }}>{riskLabel(sl)}</div>
                  </div>
                );
              })()}
            </div>

            {/* Pathways */}
            {selectedNode.pathways.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ color: td, fontSize: 9, marginBottom: 4 }}>PATHWAYS</div>
                {selectedNode.pathways.map(p => (
                  <div key={p} style={{ padding: '3px 6px', marginBottom: 2, background: 'rgba(139,92,246,0.08)', borderRadius: 4, border: '1px solid rgba(139,92,246,0.15)', color: '#8b5cf6', fontSize: 10 }}>
                    {p}
                  </div>
                ))}
              </div>
            )}

            {/* Source + Safety badges */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: selectedNode.known ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: selectedNode.known ? '#10b981' : '#ef4444' }}>
                {selectedNode.known ? 'Exact match' : 'Predicted (\u226580%)'}
              </span>
              {selectedNode.safetyFlag && (
                <span style={{ padding: '2px 6px', borderRadius: 3, fontSize: 9, background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>
                  {'\u26A0'} {selectedNode.safetyFlag}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ padding: 16, fontFamily: "'JetBrains Mono', monospace" }}>
            <div style={{ textAlign: 'center', padding: '20px 0', color: td, fontSize: 12 }}>
              Click a target sphere to view details
            </div>

            <div style={{ borderTop: `1px solid ${sb}`, paddingTop: 16, marginTop: 8 }}>
              <div style={{ color: td, fontSize: 9, marginBottom: 12, letterSpacing: '0.1em' }}>
                TARGET RISK LEGEND
              </div>
              {[
                { color: '#22d3ee', label: 'Project target', desc: 'Primary target at centre' },
                { color: '#ef4444', label: 'Risk (<10\u00D7)', desc: 'Poor selectivity' },
                { color: '#f59e0b', label: 'Watch (10-100\u00D7)', desc: 'Moderate selectivity' },
                { color: '#10b981', label: 'Safe (>100\u00D7)', desc: 'Good selectivity window' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color, boxShadow: `0 0 6px ${item.color}40`, flexShrink: 0 }} />
                  <div>
                    <div style={{ color: tp, fontSize: 11 }}>{item.label}</div>
                    <div style={{ color: td, fontSize: 9 }}>{item.desc}</div>
                  </div>
                </div>
              ))}

              <div style={{ borderTop: `1px solid ${sb}`, paddingTop: 12, marginTop: 12 }}>
                {[
                  { label: 'Wireframe spheres', desc: 'Off-target proteins' },
                  { label: 'Solid + wireframe', desc: 'Project target (centre)' },
                  { label: 'Purple lines', desc: 'Shared Reactome pathways' },
                  { label: 'Pink tint', desc: 'Safety panel (hERG, CYP)' },
                ].map((item, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <span style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: 10 }}>{item.label}</span>
                    <span style={{ color: td, fontSize: 10 }}> &mdash; {item.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TargetUniverse3D;
